/**
 * T-3 · La fiche élève — le seul écran de la console où le tuteur a le droit de
 * prendre son temps : il prépare une séance, pas une décision de trente
 * secondes.
 *
 * Ce que le Sprint 16 change par rapport à la version SLX-078 de cet écran :
 *
 *  - **la sparkline « chemin parcouru » est retirée.** Sans échelle ni légende,
 *    elle n'aide pas à décider (§14.8). Ce qui la remplace : les jours de
 *    pratique de la semaine, dénombrés, et rien d'autre ;
 *  - **la maîtrise se lit en paliers nommés et en paniers dénombrés**, jamais
 *    en pourcentage, jamais en moyenne, jamais en niveau agrégé. Les paniers
 *    par matière sont un COMPTE des savoirs observés de cette matière — pas un
 *    score d'élève ;
 *  - **l'onglet « Aides aux devoirs » est branché** (`GET /v1/tuteur/eleves/
 *    :eleveId/aides-devoirs`) : le nombre d'indices consommés dit ce que les
 *    résultats ne disent pas, et c'est le meilleur signal de blocage du
 *    produit ;
 *  - **le bandeau « ce que la famille voit » est permanent** : un tuteur qui
 *    ignore ce que la famille lit écrit maladroitement.
 *
 * Ce qui n'a pas d'endpoint est DÉCLARÉ, jamais simulé : les dernières erreurs
 * item par item, les productions de l'élève et les notes de séance privées.
 */
import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { formuleAvecPrenom, nommerJoursDePratique, nommerObservations } from '@silex/shared';

import type {
  AideDevoirsHistoriqueDto,
  EleveDuTuteurDto,
  Matiere,
  NiveauMaitrise,
  PlanApprentissageDto,
  ProgressionSavoirDto,
} from '@silex/shared';

import { api } from '../api/client';
import { DimsSavoir } from '../components/DimsSavoir';
import { EmptyState } from '../components/EmptyState';
import { Chargement, ErreurRessource, NonBranche } from '../components/EtatRessource';
import { FigureSvg } from '../components/FigureSvg';
import { LegendePalier } from '../components/PalierMaitrise';
import {
  orientationDeLaFigure,
  regrouperParSavoir,
  savoirsAFigures,
  type SavoirDeuxLectures,
} from '../lib/figures-format';
import { ancienParcoursEnPause, manuelVivantActif } from '../lib/manuel-vivant';
import { adressesManuelEleve } from '../lib/manuel-tuteur-format';
import { matiereLabel } from '../lib/suivi-format';
import {
  estASurveiller,
  formatDate,
  formatDateHeure,
  formatJoursSansSeance,
  libelleNiveauScolaire,
  libelleStatutAide,
  pluriel,
} from '../lib/tuteur-format';
import { useAsync } from '../lib/useAsync';
import { useTuteur } from '../tuteur/TuteurContext';

/** Les trois paniers, comptés sur les savoirs OBSERVÉS d'une matière. */
interface Paniers {
  readonly acquis: number;
  readonly enCours: number;
  readonly aRevoir: number;
}

function compterPaniers(savoirs: readonly ProgressionSavoirDto[]): Paniers {
  let acquis = 0;
  let enCours = 0;
  let aRevoir = 0;
  for (const s of savoirs) {
    if (s.niveauMaitrise === 'maitrise') acquis += 1;
    else if (estASurveiller(s.niveauMaitrise)) aRevoir += 1;
    else enCours += 1;
  }
  return { acquis, enCours, aRevoir };
}

/** Les paliers les plus fragiles d'abord : c'est l'ordre de préparation d'une séance. */
const ORDRE_PALIER: Readonly<Record<NiveauMaitrise, number>> = {
  non_evalue: 0,
  en_decouverte: 1,
  en_consolidation: 2,
  en_voie_de_maitrise: 3,
  maitrise: 4,
};

/** Le statut d'une évaluation, dit comme un tuteur le lit. */
const LIBELLE_EVALUATION: Readonly<Record<string, string>> = {
  en_cours: 'Évaluation en cours',
  terminee: 'Évaluation terminée',
  abandonnee: 'Évaluation abandonnée',
};

export function DetailElevePage() {
  const { id } = useParams<'id'>();
  const eleveId = id && id !== 'apercu' ? id : null;
  const { eleves } = useTuteur();
  /* Chantier « manuel vivant », Φ-MV-7 (`UX-7-14`, décision F-32) — l'ancien
     parcours en pause, la fiche du manuel EST la fiche de l'élève : plus rien
     ne renvoie à cette page, qui reste servie à son adresse. Ouverte ainsi,
     elle renvoie à la fiche, et ne montre ni code interne ni section qui
     n'affiche qu'une explication technique. Hors pause : rien ne change. */
  const manuel = ancienParcoursEnPause();

  const progression = useAsync(
    useMemo(() => (eleveId ? () => api.getProgression(eleveId) : null), [eleveId]),
  );
  const recommandations = useAsync(
    useMemo(() => (eleveId ? () => api.getRecommandations(eleveId) : null), [eleveId]),
  );
  const dose = useAsync(
    useMemo(() => (eleveId ? () => api.getDose(eleveId, 'semaine') : null), [eleveId]),
  );
  const aides = useAsync(
    useMemo(() => (eleveId ? () => api.getAidesDevoirs(eleveId) : null), [eleveId]),
  );
  const plan = useAsync(
    useMemo(() => (eleveId ? () => api.getPlanActif(eleveId) : null), [eleveId]),
  );
  /* L'HISTORIQUE DES ÉVALUATIONS (14 septembre 2026). La console avait la
     méthode cliente — `getEvaluationsDiagnostiques` — et aucune page ne
     l'appelait : le tuteur voyait la file des évaluations À VALIDER, jamais
     celles déjà passées. Le fondateur l'a relevé en recette, en même temps que
     les copies annotées. */
  const evaluations = useAsync(
    useMemo(() => (eleveId ? () => api.getEvaluationsDiagnostiques(eleveId) : null), [eleveId]),
  );

  const eleve: EleveDuTuteurDto | undefined =
    eleves.status === 'ready' ? eleves.data.eleves.find((e) => e.eleveId === eleveId) : undefined;

  const matieres: readonly Matiere[] = eleve?.matieresActives.length
    ? eleve.matieresActives
    : (['francais', 'mathematiques'] as const);
  const [matiere, setMatiere] = useState<Matiere | null>(null);
  const matiereActive: Matiere = matiere ?? matieres[0] ?? 'francais';

  if (!eleveId) {
    return (
      <div className="content__inner">
        <div className="page-head">
          <div>
            <p className="breadcrumb">
              <Link to="/ma-journee">Ma journée</Link> / <Link to="/eleves">Mes élèves</Link>
            </p>
            <h1>Fiche élève</h1>
          </div>
        </div>
        <EmptyState title="Aucun élève sélectionné">
          Ouvrez un élève depuis « Mes élèves » ou depuis une unité de votre file du matin.
        </EmptyState>
      </div>
    );
  }

  const savoirsMatiere =
    progression.status === 'ready'
      ? [...progression.data.savoirs]
          .filter((s) => s.matiere === matiereActive)
          .sort((a, b) => ORDRE_PALIER[a.niveauMaitrise] - ORDRE_PALIER[b.niveauMaitrise])
      : [];
  const paniers = compterPaniers(savoirsMatiere);
  const savoirsGroupes = regrouperParSavoir(savoirsMatiere);
  const figuresPossibles = savoirsAFigures(savoirsGroupes);

  const objectifsMatiere: readonly PlanApprentissageDto['objectifs'][number][] =
    plan.status === 'ready' && plan.data.plan
      ? plan.data.plan.objectifs.filter((o) => o.matiere === matiereActive)
      : [];

  return (
    <div className="content__inner">
      <div className="dash-topbar">
        <div>
          <p className="breadcrumb">
            <Link to="/ma-journee">Ma journée</Link> / <Link to="/eleves">Mes élèves</Link> /{' '}
            {manuel ? (
              <>
                <Link to={adressesManuelEleve.fiche(eleveId)} data-testid="detail-eleve-fil-fiche">
                  {eleve?.prenom ?? 'élève'}
                </Link>{' '}
                / ancien suivi par savoir
              </>
            ) : (
              (eleve?.prenom ?? 'élève')
            )}
          </p>
          <h1>
            {eleve
              ? `${eleve.prenom} · ${libelleNiveauScolaire(eleve.niveauScolaire)}`
              : 'Fiche élève'}
          </h1>
          {eleve ? (
            <p className="page-sub">
              Suivi depuis le {formatDate(eleve.affecteDepuis)} ·{' '}
              {eleve.matieresActives.map(matiereLabel).join(' et ').toLowerCase() ||
                'aucune matière active'}{' '}
              · {formatJoursSansSeance(eleve.joursSansSeance)}
            </p>
          ) : null}
        </div>
        <div className="row wrap">
          {/* Chantier « manuel vivant », Φ-MV-5 — l'entrée de la fiche de
              l'élève dans son chapitre (objectif, portrait, séquences,
              séances). Drapeau éteint, le bouton n'existe pas. */}
          {manuelVivantActif() ? (
            <Link
              className="btn btn--ghost btn--sm"
              to={adressesManuelEleve.fiche(eleveId)}
              data-testid="detail-eleve-manuel"
            >
              {manuel ? 'Sa fiche' : 'Son chapitre'}
            </Link>
          ) : null}
          <Link className="btn btn--ghost btn--sm" to={`/eleves/${eleveId}/copies`}>
            Travaux corrigés
          </Link>
          <Link className="btn btn--ghost btn--sm" to={`/rapports/${eleveId}`}>
            Son rapport
          </Link>
          <Link
            className="btn btn--primary btn--sm"
            to={`/exercices/nouveau?eleve=${eleveId}&matiere=${matiereActive}`}
          >
            Créer un exercice ciblé
          </Link>
        </div>
      </div>

      <div className="callout callout--calm">
        <strong>Ce que la famille voit de cette fiche.</strong> Son parent voit les savoirs nommés,
        les paliers en langage courant, le plan et vos rapports. Il ne voit ni cette page, ni le
        détail des indices consommés. L'élève voit sa progression et vos commentaires attachés à ses
        travaux. Aucun message ne circule : ce que vous avez à dire à la famille passe par son
        rapport.
      </div>

      {manuel ? (
        <p className="note-line" data-testid="detail-eleve-renvoi">
          Cette page est l’ancien suivi de {eleve?.prenom ?? 'cet élève'}, savoir par savoir. Son
          objectif, ses erreurs relevées, ses travaux et vos séances se lisent dans{' '}
          <Link to={adressesManuelEleve.fiche(eleveId)}>sa fiche</Link>.
        </p>
      ) : null}

      <div className="segmented" role="group" aria-label="Matière">
        {matieres.map((m) => (
          <button
            key={m}
            type="button"
            className={m === matiereActive ? 'segmented__btn is-active' : 'segmented__btn'}
            onClick={() => setMatiere(m)}
          >
            {matiereLabel(m)}
          </button>
        ))}
      </div>

      <div className="tuteur-grid">
        <div className="stack">
          <section className="card">
            <div className="row-between">
              {/* LOT 3.2 — l'accord suit l'élève : le prénom, jamais un pronom
                  (MOB-PAR-P2-02, motif transverse aux trois surfaces). */}
              <h3>
                {formuleAvecPrenom('Où en est {prenom}', eleve?.prenom, 'cet élève')} —{' '}
                {matiereLabel(matiereActive).toLowerCase()}
              </h3>
              <span className="card-sub">
                {pluriel(savoirsGroupes.length, 'savoir observé', 'savoirs observés')} ·{' '}
                {pluriel(savoirsMatiere.length, 'mesure', 'mesures')}
              </span>
            </div>

            {progression.status === 'loading' ? <Chargement quoi="la progression" /> : null}
            {progression.status === 'error' ? (
              <ErreurRessource message={progression.message} />
            ) : null}

            {progression.status === 'ready' && savoirsMatiere.length === 0 ? (
              <p className="note-line">Aucun savoir observé dans cette matière.</p>
            ) : null}

            {savoirsMatiere.length > 0 ? (
              <>
                <div className="paniers-row">
                  <div className="stat-tile">
                    <span className="stat-tile__value">{paniers.acquis}</span>
                    <span className="stat-tile__label">acquis</span>
                  </div>
                  <div className="stat-tile">
                    <span className="stat-tile__value">{paniers.enCours}</span>
                    <span className="stat-tile__label">en cours</span>
                  </div>
                  <div className="stat-tile stat-tile--watch">
                    <span className="stat-tile__value">{paniers.aRevoir}</span>
                    <span className="stat-tile__label">à revoir</span>
                  </div>
                </div>

                <div className="savoir-liste">
                  {savoirsGroupes.map((s) => (
                    <div key={s.savoirId} className="savoir-row savoir-row--dims">
                      <div className="savoir-row__main">
                        <span className="savoir-row__enonce">{s.enonce}</span>
                        <span className="savoir-row__matiere">
                          {/* Un code de savoir n'est pas un mot de tuteur (`UX-7-14`). */}
                          {manuel ? null : s.code}
                          {s.derniereObservation
                            ? `${manuel ? 'Observé' : ' · observé'} le ${formatDate(s.derniereObservation)}`
                            : ''}
                          {manuel && !s.derniereObservation ? null : ' · '}
                          {/* LOT 1.3 (BD-02) — « 9 observations » ici et
                              « 3 observations » sur l'écran atteint en tapant
                              cette ligne : deux chiffres justes, aucun moyen
                              de les rapprocher. Le total dit sa
                              décomposition. */}
                          {nommerObservations(s.nbObservations, s.nbObservationsParDimension)}
                        </span>
                        {/* Les deux lectures, côte à côte et jamais fondues.
                            Un savoir visuel en porte deux ; un savoir
                            d'algèbre en porte une, et l'écran le nomme. */}
                        <DimsSavoir
                          paliers={s.paliers}
                          porteLaLectureDeFigure={s.porteLaLectureDeFigure}
                        />
                      </div>
                    </div>
                  ))}
                </div>
                <LegendePalier />
                <p className="note-line">
                  Ces paniers dénombrent des mesures, pas des élèves : un savoir sur figure en porte
                  deux, et elles ne s'additionnent pas.
                </p>
              </>
            ) : null}
          </section>

          {/* ---- G4 · « Les dernières figures de … » ---------------------
                  Le flux « Silex → tuteur, montrer » n'existait pas : ni le
                  tuteur ni le parent ne voyaient jamais ce que l'élève avait
                  lu. C'est ici que le tuteur cesse d'arbitrer un verdict pour
                  commencer à lire une TRAJECTOIRE — et ce qu'aucun décompte
                  « 2 sur 3 » ne porte à sa place. ---- */}
          <DernieresFigures
            eleveId={eleveId}
            prenom={eleve?.prenom ?? "l'élève"}
            savoirs={figuresPossibles}
            sansCode={manuel}
          />

          <AidesDevoirs
            aides={aides.status === 'ready' ? aides.data.aides : []}
            chargement={aides.status === 'loading'}
            erreur={aides.status === 'error' ? aides.message : null}
            sansCode={manuel}
          />

          {manuel ? (
            /* Ses erreurs, ses travaux et vos notes de séance vivent dans sa
               fiche : trois liens, plutôt que trois encadrés qui expliquaient
               pourquoi cette page ne les montrait pas. */
            <section className="card" data-testid="detail-eleve-dans-sa-fiche">
              <h3>Dans sa fiche</h3>
              <div className="list list--flush">
                <Link className="li" to={adressesManuelEleve.fiche(eleveId)}>
                  <span className="li-main">
                    <span className="li-title">Ses erreurs relevées</span>
                    <span className="li-sub">
                      Portrait : chaque erreur, sa lecture, quoi faire.
                    </span>
                  </span>
                </Link>
                <Link className="li" to={adressesManuelEleve.fiche(eleveId, 'travaux')}>
                  <span className="li-main">
                    <span className="li-title">Ses travaux et leurs corrections</span>
                    <span className="li-sub">Exercices et devoirs, ligne par ligne.</span>
                  </span>
                </Link>
                <Link className="li" to={adressesManuelEleve.fiche(eleveId, 'seances')}>
                  <span className="li-main">
                    <span className="li-title">Vos notes de séance</span>
                    <span className="li-sub">Lues par vous seul.</span>
                  </span>
                </Link>
              </div>
            </section>
          ) : (
            <NonBranche titre="Ses dernières erreurs">
              Ce que vous regardez avant une séance — les erreurs, pas les scores — demande le
              détail des tentatives item par item. Aucune route de l'API ne l'expose aujourd'hui :
              ni `TentativeExercice` ni le contenu d'une réponse ne sont servis à la console. Rien
              n'est affiché ici plutôt qu'un exemple fabriqué.
            </NonBranche>
          )}
        </div>

        <div className="stack">
          <section className="card">
            <h3>Plan d'apprentissage</h3>
            {plan.status === 'loading' ? <Chargement quoi="le plan" /> : null}
            {plan.status === 'error' ? <ErreurRessource message={plan.message} /> : null}
            {plan.status === 'ready' && !plan.data.plan ? (
              <p className="note-line">Aucun plan de référence approuvé pour cet élève.</p>
            ) : null}
            {plan.status === 'ready' && plan.data.plan ? (
              <>
                <p className="card-sub">
                  {formatDate(plan.data.plan.dateDebut)} → {formatDate(plan.data.plan.dateFin)}
                  {plan.data.plan.approuveLe
                    ? ` · approuvé le ${formatDate(plan.data.plan.approuveLe)}`
                    : ' · pas encore approuvé'}
                </p>
                {objectifsMatiere.length === 0 ? (
                  <p className="note-line">Aucun objectif de ce plan ne porte sur cette matière.</p>
                ) : (
                  <div className="stack-sm objectifs">
                    {objectifsMatiere.map((o) => (
                      <div key={o.id} className="objectif">
                        <div className="row-between wrap">
                          <span className="objectif__nom">
                            {o.ordre} · {o.description}
                          </span>
                          <span
                            className={
                              o.statut === 'atteint'
                                ? 'badge badge--acquis'
                                : 'badge badge--en-cours'
                            }
                          >
                            {o.statut === 'atteint' ? 'Atteint' : 'En cours'}
                          </span>
                        </div>
                        <span className="objectif__meta">
                          Échéance : {formatDate(o.dateCible)}
                          {o.atteintLe ? ` · atteint le ${formatDate(o.atteintLe)}` : ''}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : null}
          </section>

          <section className="card">
            <h3>Ses évaluations</h3>
            {evaluations.status === 'loading' ? <Chargement quoi="ses évaluations" /> : null}
            {evaluations.status === 'error' ? (
              <ErreurRessource message={evaluations.message} />
            ) : null}
            {evaluations.status === 'ready' && evaluations.data.evaluations.length === 0 ? (
              <p className="note-line">Aucune évaluation passée pour cet élève.</p>
            ) : null}
            {evaluations.status === 'ready' && evaluations.data.evaluations.length > 0 ? (
              <div className="list">
                {evaluations.data.evaluations.map((e) => (
                  <div className="li" key={e.id}>
                    <span className="li-main">
                      <span className="li-title">{LIBELLE_EVALUATION[e.statut] ?? e.statut}</span>
                      <span className="li-sub">
                        {formatDate(e.demarreeLe)}
                        {e.termineeLe ? ` → ${formatDate(e.termineeLe)}` : ' · en cours'}
                        {e.rapportId ? ' · bilan produit' : ''}
                      </span>
                    </span>
                  </div>
                ))}
              </div>
            ) : null}
          </section>

          <section className="card card--calm">
            <h3>Recommandation du système</h3>
            {recommandations.status === 'loading' ? (
              <Chargement quoi="les recommandations" />
            ) : null}
            {recommandations.status === 'error' ? (
              <ErreurRessource message={recommandations.message} />
            ) : null}
            {recommandations.status === 'ready' ? (
              recommandations.data.recommandations.length === 0 ? (
                <p className="note-line">
                  Aucune intervention recommandée. {recommandations.data.critere}
                </p>
              ) : (
                <>
                  <ul className="recommandations-list">
                    {recommandations.data.recommandations.map((r) => (
                      <li key={r.savoirId} className="recommandation-card">
                        <strong>{r.enonce}</strong> — {matiereLabel(r.matiere).toLowerCase()}, sans
                        nouvelle observation depuis {r.joursSansProgres} jours.
                      </li>
                    ))}
                  </ul>
                  <p className="note-line">{recommandations.data.critere}</p>
                </>
              )
            ) : null}
          </section>

          <section className="card">
            <h3>Sa semaine</h3>
            {dose.status === 'loading' ? <Chargement quoi="la dose de travail" /> : null}
            {dose.status === 'error' ? <ErreurRessource message={dose.message} /> : null}
            {dose.status === 'ready' ? (
              <>
                <div className="jours-actifs" aria-hidden="true">
                  {dose.data.historique.slice(-7).map((jour) => (
                    <span
                      key={jour.date}
                      // LOT 1.3 — un jour est actif dès qu'il porte du
                      // travail, pas seulement un exercice : une aide aux
                      // devoirs en est. Le serveur le dit (`actif`), la console
                      // ne le redéduit plus d'un compteur d'exercices.
                      className={jour.actif ? 'jour is-actif' : 'jour'}
                      title={`${jour.date} — ${pluriel(jour.nbTentatives, 'exercice')}`}
                    />
                  ))}
                </div>
                <p className="card-sub">
                  {/* LOT 1.6 — le nom du fait vient de @silex/shared/editorial. */}
                  {nommerJoursDePratique(dose.data.nbJoursActifs)} ·{' '}
                  {pluriel(dose.data.nbTentatives, 'exercice')} depuis le{' '}
                  {formatDate(dose.data.depuisLe)}
                </p>
              </>
            ) : null}
          </section>

          {manuel ? null : (
            <>
              <NonBranche titre="Ses productions">
                Les travaux déposés par l'élève (photos, canevas, textes) demandent des URL signées
                à courte durée (SEC-09). Aucune route ne les sert à la console à ce jour.
              </NonBranche>

              <NonBranche titre="Vos notes de séance">
                Rien ne persiste une note privée de tuteur au schéma. Un champ qui ne s'enregistre
                pas est pire qu'un champ absent : il fait perdre ce qu'on y écrit.
              </NonBranche>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------- G4 · les figures */

/**
 * Les dernières figures d'un élève sur un savoir — G4 de la maquette v5.
 *
 * Chaque figure est RECONSTRUITE depuis sa spécification bornée : il n'y a
 * aucune image à stocker ni à transmettre, et elle suit le thème de la console
 * comme celui de l'application. La lecture déclarée est indiquée sous chacune,
 * avec le verdict de la LECTURE SEULE — une lecture à revoir ne dit rien du
 * calcul, qui est jugé à part.
 *
 * Registre : vouvoiement. C'est un écran d'adulte professionnel.
 */
function DernieresFigures({
  eleveId,
  prenom,
  savoirs,
  sansCode,
}: {
  eleveId: string;
  prenom: string;
  savoirs: readonly SavoirDeuxLectures[];
  /** Capacité « manuel vivant » allumée : un savoir se dit par son énoncé, jamais par son code. */
  sansCode: boolean;
}) {
  const [savoirId, setSavoirId] = useState<string | null>(null);
  const savoirActif = savoirs.find((s) => s.savoirId === savoirId) ?? savoirs[0] ?? null;
  const journal = useAsync(
    useMemo(
      () => (savoirActif ? () => api.getObservations(eleveId, savoirActif.savoirId) : null),
      [eleveId, savoirActif],
    ),
  );

  if (savoirs.length === 0) {
    return (
      <section className="card">
        <h3>Ses dernières figures</h3>
        <p className="note-line">Aucune lecture de figure observée pour cet élève.</p>
      </section>
    );
  }

  const observations =
    journal.status === 'ready'
      ? journal.data.observations.filter((o) => o.dimension === 'lecture_figure').slice(0, 3)
      : [];

  return (
    <section className="card">
      <div className="row-between">
        <h3>Les dernières figures de {prenom}</h3>
        {savoirActif && !sansCode ? <span className="data">{savoirActif.code}</span> : null}
      </div>
      <p className="card-sub">
        Chaque figure est reconstruite depuis sa spécification bornée. La lecture déclarée par{' '}
        {prenom} est indiquée sous chacune.
      </p>

      {savoirs.length > 1 ? (
        <div className="filters" role="group" aria-label="Choisir le savoir">
          {savoirs.map((s) => (
            <button
              key={s.savoirId}
              type="button"
              className={s.savoirId === savoirActif?.savoirId ? 'chip-filter is-on' : 'chip-filter'}
              aria-pressed={s.savoirId === savoirActif?.savoirId}
              onClick={() => setSavoirId(s.savoirId)}
            >
              {sansCode ? s.enonce : s.code}
            </button>
          ))}
        </div>
      ) : null}

      {journal.status === 'loading' ? <Chargement quoi="les figures" /> : null}
      {journal.status === 'error' ? <ErreurRessource message={journal.message} /> : null}

      {journal.status === 'ready' && observations.length === 0 ? (
        <p className="note-line">
          Ce savoir porte une mesure de lecture de figure, mais aucune figure n'est encore au
          journal.
        </p>
      ) : null}

      {observations.length > 0 ? (
        <>
          {/* La grille s'affiche avec ce qu'elle a. Avec moins de trois
              figures, elle ne remplit AUCUNE case et la note d'écart
              disparaît — jamais une figure inventée pour compléter. */}
          <div className="fig-serie">
            {observations.map((o) =>
              o.figureSpec ? (
                <FigureSvg
                  key={o.id}
                  spec={o.figureSpec}
                  variante={o.lectureDeclaree?.correcte === false ? 'eleve' : 'juste'}
                  etiquetteSurface={formatDate(o.creeLe)}
                  legendeComplement={
                    <>
                      {/* Vu en capture : la description dit déjà « la figure
                          est posée de biais » quand elle l'est. On ne le
                          répète pas — on ne nomme l'orientation que dans le
                          cas où la description se tait, celui qui compte le
                          plus pour le tuteur. */}
                      {orientationDeLaFigure(o) === 'Orientation habituelle' ? (
                        <b>Orientation habituelle. </b>
                      ) : null}
                      {o.lectureDeclaree ? (
                        <>
                          {'Lecture déclarée : '}
                          <b>{o.lectureDeclaree.libelle}</b>
                          {o.lectureDeclaree.correcte ? ' — juste.' : ' — à revoir.'}
                        </>
                      ) : null}
                    </>
                  }
                />
              ) : null,
            )}
          </div>

          {observations.length >= 3 ? (
            <div className="anchored-note">
              <b>
                Ce que la série montre, et que « {observations.filter((o) => o.correct).length} sur{' '}
                {observations.length} » ne montre pas :
              </b>{' '}
              une réussite sur une figure en orientation habituelle est celle où la règle apprise et
              l'allure du dessin donnent la même réponse — elle ne les distingue pas. Ce sont les
              figures posées de biais, dans des orientations différentes, qui tranchent.
            </div>
          ) : null}

          <p className="note-line">
            {observations.length === 1
              ? 'Une observation, un fait daté.'
              : `${observations.length} observations, ${observations.length} faits datés.`}{' '}
            Aucune moyenne n'est calculée entre elles, et la lecture n'est jamais fondue avec le
            calcul : les deux mesures se décident séparément.
          </p>
        </>
      ) : null}

      {savoirActif ? (
        <>
          <div className="hr-soft" />
          <span className="stage-label">{sansCode ? savoirActif.enonce : savoirActif.code}</span>
          <DimsSavoir
            paliers={savoirActif.paliers}
            porteLaLectureDeFigure={savoirActif.porteLaLectureDeFigure}
          />
          <p className="note-line">
            La décision de tuteur par dimension n'a pas encore de chemin d'écriture : elle n'est pas
            proposée ici.
          </p>
        </>
      ) : null}
    </section>
  );
}

/* ----------------------------------------------------- aides aux devoirs */

/**
 * « Le nombre d'indices consommés dit ce que les résultats ne disent pas. » Un
 * élève qui épuise l'échelle sans arriver au bout a un problème que
 * l'entraînement ne réglera pas.
 *
 * Pas de colonne « issue » : `AideDevoirsRequete` ne la persiste pas, et on
 * n'expose pas une donnée qui n'existe pas au contrat.
 */
function AidesDevoirs({
  aides,
  chargement,
  erreur,
  sansCode,
}: {
  aides: readonly AideDevoirsHistoriqueDto[];
  chargement: boolean;
  erreur: string | null;
  sansCode: boolean;
}) {
  const bloquantes = aides.filter((a) => a.revelationDebloquee).length;
  return (
    <section className="card">
      <div className="row-between">
        <h3>Aides aux devoirs</h3>
        {bloquantes > 0 ? <span className="badge badge--a-revoir">Signal de blocage</span> : null}
      </div>
      {chargement ? <Chargement quoi="l'historique d'aides" /> : null}
      {erreur ? <ErreurRessource message={erreur} /> : null}
      {!chargement && !erreur && aides.length === 0 ? (
        <p className="note-line">Aucune session d'aide aux devoirs enregistrée pour cet élève.</p>
      ) : null}

      {aides.length > 0 ? (
        <>
          <div className="table-wrap">
            <table className="data-table data-table--compact">
              <thead>
                <tr>
                  <th scope="col">Quand</th>
                  <th scope="col">Sur quoi</th>
                  <th scope="col">Indices consommés</th>
                  <th scope="col">Échanges</th>
                  <th scope="col">Statut</th>
                </tr>
              </thead>
              <tbody>
                {aides.map((a) => (
                  <tr key={a.id}>
                    <td>{formatDateHeure(a.demarreeLe)}</td>
                    <td>
                      {a.savoir ? a.savoir.enonce : matiereLabel(a.matiere)}
                      {a.savoir && !sansCode ? (
                        <span className="cell-sub">{a.savoir.code}</span>
                      ) : null}
                    </td>
                    <td>
                      <span className={a.revelationDebloquee ? 'data data--watch' : 'data'}>
                        {a.indicesConsommes} sur {a.indicesTotal}
                      </span>
                      {a.revelationDebloquee ? (
                        <span className="cell-sub">révélation débloquée</span>
                      ) : null}
                    </td>
                    <td>
                      <span className="data">{a.nbEchangesQuestion}</span>
                    </td>
                    <td>{libelleStatutAide(a.statut)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="note-line">
            La révélation est le quatrième palier, débloqué sur confirmation explicite de l'élève.
          </p>
        </>
      ) : null}
    </section>
  );
}
